import { Fragment, JSX, useCallback } from 'react';
import styled from 'styled-components';
import { orgHymnalDefaultIconPath } from '../../../common/src/paths.ts';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { HymnalWithHymns } from '../data/hymnals.ts';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { PrimaryOrgHymnalId } from '../util/path.ts';
import { hymnalEntryPadding, HymnalIcon, HymnalIconContainer, hymnalIconWidth } from '../util/shared.tsx';
import { SyncAnimation } from '../util/sync_animation.tsx';

const hymnalSyncAnimationWidth = `calc(${hymnalIconWidth} * 2 / 3)`;

export function HymnalsView({
  hymnals,
  onHymnalClick,
  songClickIsEnabled,
  singleton,
  headerAfterFirstHymnal,
  bottomBorder,
}: {
  hymnals: HymnalWithHymns[];
  onHymnalClick: (hymnal: HymnalWithHymns) => void;
  songClickIsEnabled: (hymnal?: HymnalWithHymns) => boolean;
  singleton?: boolean;
  headerAfterFirstHymnal?: JSX.Element;
  bottomBorder?: boolean;
}) {
  return (
    <HymnalsViewWrapper>
      {hymnals.map((hymnal, index) => (
        <Fragment key={hymnal.name}>
          <HymnalsViewEntry
            hymnal={hymnal}
            index={index}
            onClick={onHymnalClick}
            songClickIsEnabled={songClickIsEnabled}
            hymnalCount={hymnals.length}
            singleton={singleton}
            bottomBorder={bottomBorder}
          />
          {headerAfterFirstHymnal && index === 0 && headerAfterFirstHymnal}
        </Fragment>
      ))}
    </HymnalsViewWrapper>
  );
}

const HymnalsViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export function HymnalsViewEntry({
  hymnal,
  index,
  singleton,
  onClick,
  songClickIsEnabled,
  hymnalCount,
  bottomBorder,
}: {
  hymnal: HymnalWithHymns;
  index: number;
  singleton?: boolean;
  onClick: (hymnal: HymnalWithHymns) => void;
  songClickIsEnabled?: (hymnal?: HymnalWithHymns) => boolean;
  hymnalCount: number;
  bottomBorder?: boolean;
}) {
  const { name } = hymnal;

  const onClickWrapper = useCallback(() => {
    onClick(hymnal);
  }, [hymnal, onClick]);

  const [orgHymnalDownloadHash] = useLocalStorage(LocalStorageKey.OrgHymnalDownloadHashingHash);
  const isSynchronizingHymnal = Boolean(orgHymnalDownloadHash) && hymnal.id === PrimaryOrgHymnalId;

  return (
    <Fragment>
      <HymnalWrapper onClick={onClickWrapper}>
        <HymnalIconContainer>
          <HymnalIcon
            $hasBorder={!!hymnal.icon?.hasBorder}
            $hasWash={isSynchronizingHymnal}
            src={hymnal.icon?.path ?? orgHymnalDefaultIconPath}
          />
          {isSynchronizingHymnal && <HymnalSyncAnimation $size={hymnalSyncAnimationWidth} />}
        </HymnalIconContainer>
        <HymnalDescription>
          <HymnalTitle
            style={{
              color:
                songClickIsEnabled && !songClickIsEnabled(hymnal) ? 'var(--color-text-disabled)' : 'var(--color-text)',
              ...(singleton && {
                fontFamily: 'Jost-SemiBold, Arial, sans-serif',
                fontSize: '1.2em',
              }),
            }}
          >
            {name}
          </HymnalTitle>
          <HymnalPublisher>{hymnal.publisher}</HymnalPublisher>
        </HymnalDescription>
      </HymnalWrapper>
      {(singleton || index === hymnalCount - 1) && !bottomBorder ? null : (
        <HymnalDividerWrapper $extraSpace={bottomBorder}>
          <HymnalDividerSpacer />
          <HymnalDivider />
        </HymnalDividerWrapper>
      )}
    </Fragment>
  );
}

const HymnalWrapper = styled.div`
  display: flex;
  font-family: Jost-Regular, Arial, sans-serif;
  font-size: large;
  cursor: pointer;
  padding: 3px 3px;
`;

const HymnalTitle = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const HymnalPublisher = styled.div`
  padding-top: 0.15em;
  color: var(--color-text-light);
  font-family: Jost-Regular, Arial, sans-serif;
  font-size: medium;
`;

const HymnalDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0.4em;
  text-align: left;
  max-width: calc(100vw - 75px);
`;

const HymnalSyncAnimation = styled(SyncAnimation)`
  fill: var(--color-background);
  position: absolute;
  top: calc(${hymnalIconWidth} / 6);
  left: calc(${hymnalIconWidth} / 6);
`;

const HymnalDividerWrapper = styled.div<{ $extraSpace?: boolean }>`
  display: flex;
  ${(props) => (props.$extraSpace ? 'padding: 2px 0' : '')};
`;

const HymnalDividerSpacer = styled.div`
  width: calc(${hymnalIconWidth} + ${hymnalEntryPadding} * 2 + 13px);
`;

const HymnalDivider = styled.div`
  border-bottom: 0.8px solid var(--color-text-light);
  opacity: 50%;
  margin: 0;
  width: 100%;
`;
